import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import ConfirmationModal from '../modals/ConfirmationModal';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import {
  COMPLAINT_DOCUMENT_TYPE_PHOTO,
  COMPLAINT_DOCUMENT_TYPE_REPORT,
  DOCUMENT_OPTIONS,
  formatDocumentType,
  TECHNICIAN_WORK_TYPE_GENERAL,
} from './DocumentTypes';
import { ComplaintIDBadge, StatusBadge } from './Badges';
import useEnvironmentCameraSupport from '../../hooks/useEnvironmentCameraSupport';
import './DocumentManager.css';

function DocumentManager({
  deviceId,
  /** When set, uses complaint document APIs instead of device documents. */
  complaintId,
  /**
   * With deviceId (and without complaintId): list other complaints' documents on
   * this device, excluding the given complaint (read-only device history).
   */
  excludeComplaintId,
  token,
  onDocumentCountChange,
  user,
  deviceStatus,
  isSubmittedForApproval,
  documentOptions,
  allowedDocumentTypes,
  /** If set, upload type dropdown uses only these options; list still uses allowedDocumentTypes. */
  uploadDocumentOptions,
  permissions,
  deleteOnlyOwn,
  /** When true, delete is only offered for documents added after the first list load (e.g. this modal session). */
  restrictDeleteToNewSinceInitialLoad,
  /**
   * When true, the list shows only documents uploaded after the first fetch
   * (e.g. maintenance logging "this session" section).
   */
  showOnlySessionDocuments = false,
  /**
   * When true, the list shows only documents present on the first fetch
   * (e.g. maintenance logging "previous reports" section). Mutually exclusive
   * with showOnlySessionDocuments.
   */
  showOnlyBaselineDocuments = false,
  /** Disambiguate DOM ids when multiple managers share the same device/complaint. */
  instanceId = '',
  /** If set, sent as multipart work_type so the API can enforce document_type (maintenance, complaint close, etc.). */
  workType,
  /** When restrictDeleteToNewSinceInitialLoad is used, called after each successful list fetch with count of documents not in the initial baseline. */
  onPastBaselineDocumentCountChange,
  /** Optional query filter for list (e.g. document_type=complaint_report). */
  listDocumentType,
  /** Override empty-list copy (device vs complaint context). */
  emptyStateText,
  /** Show a Take photo control (device camera / capture) alongside Select File. */
  enableCameraCapture = false,
}) {
  const isComplaintMode = !!complaintId;
  const isDeviceComplaintHistoryMode = !!(deviceId && excludeComplaintId && !complaintId);
  const entityKey = [
    isComplaintMode
      ? `complaint-${complaintId}`
      : isDeviceComplaintHistoryMode
        ? `device-${deviceId}-excl-${excludeComplaintId}`
        : `device-${deviceId}`,
    instanceId,
  ].filter(Boolean).join('-');
  const fileInputId = `file-input-${entityKey}`;
  const cameraInputId = `camera-input-${entityKey}`;
  const tracksSessionBaseline =
    restrictDeleteToNewSinceInitialLoad
    || showOnlySessionDocuments
    || showOnlyBaselineDocuments;
  const {
    checking: cameraChecking,
    available: rearCameraAvailable,
    reason: cameraUnavailableReason,
  } = useEnvironmentCameraSupport(enableCameraCapture);
  const takePhotoEnabled = enableCameraCapture && rearCameraAvailable && !cameraChecking;
  const takePhotoTitle = cameraChecking
    ? 'Checking camera…'
    : (cameraUnavailableReason || 'No rear camera available on this device');

  const listUrl = () => {
    if (isDeviceComplaintHistoryMode) {
      return API_ENDPOINTS.COMPLAINTS.DEVICE_COMPLAINT_DOCUMENTS(deviceId, excludeComplaintId);
    }
    if (isComplaintMode) {
      const base = API_ENDPOINTS.COMPLAINTS.DOCUMENTS(complaintId);
      return listDocumentType
        ? `${base}?document_type=${encodeURIComponent(listDocumentType)}`
        : base;
    }
    return API_ENDPOINTS.DEVICES.DOCUMENTS(deviceId);
  };
  const uploadUrl = () => (
    isComplaintMode
      ? API_ENDPOINTS.COMPLAINTS.DOCUMENTS(complaintId)
      : API_ENDPOINTS.DEVICES.DOCUMENTS(deviceId)
  );
  const resolveDocumentUrl = (doc) => {
    if (isDeviceComplaintHistoryMode) {
      return API_ENDPOINTS.COMPLAINTS.DOCUMENT(doc.complaint_id, doc.id);
    }
    if (isComplaintMode) {
      return API_ENDPOINTS.COMPLAINTS.DOCUMENT(complaintId, doc.id);
    }
    return API_ENDPOINTS.DOCUMENTS.GET(deviceId, doc.id);
  };

  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState(null);
  const [showPendingPreview, setShowPendingPreview] = useState(false);
  const [documentType, setDocumentType] = useState('manual');
  const [docTypeOpen, setDocTypeOpen] = useState(false);
  const [docTypeMenuStyle, setDocTypeMenuStyle] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedDocumentForPreview, setSelectedDocumentForPreview] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [isDeletingDocument, setIsDeletingDocument] = useState(false);
  const managerRef = useRef(null);
  const docTypeRef = useRef(null);
  const docTypeMenuRef = useRef(null);
  const pendingPreviewUrlRef = useRef(null);
  /** Set once after first successful GET for this entity when restrictDeleteToNewSinceInitialLoad is true. */
  const sessionBaselineDocumentIdsRef = useRef(null);

  // Check if user can manage documents (upload/delete)
  const canManageDocuments = user && ['admin', 'superadmin', 'coordinator', 'manager'].includes(user.role);
  // Restrict preview/download for central_help_desk role
  const canPreviewDocuments = user && user.role !== 'central_help_desk';
  // For managers creating new devices, restrict uploads until device is approved
  const isDevicePendingApproval = deviceStatus === 'pending_approval';
  // When device is submitted for approval, fully disable all document management (upload and delete)
  // Check if device is awaiting approval review (device submitted to admin for approval)
  const isAwaitingApprovalReview = !!isSubmittedForApproval || isDevicePendingApproval;
  // Managers cannot upload until device is approved, but admin/coordinator can
  // Also disable uploads when device is awaiting approval review
  const canUploadDocuments = canManageDocuments && !(isDevicePendingApproval && user?.role === 'manager') && !isAwaitingApprovalReview;
  // Disable delete operations when device is awaiting approval review
  const canDeleteDocuments = canManageDocuments && !isAwaitingApprovalReview;

  // Device uploads must not offer complaint-scoped types (those live on complaints).
  const complaintScopedDocumentTypes = new Set([
    COMPLAINT_DOCUMENT_TYPE_PHOTO,
    COMPLAINT_DOCUMENT_TYPE_REPORT,
  ]);
  const resolvedDocumentOptions = (documentOptions ?? DOCUMENT_OPTIONS).filter((opt) => {
    if (isComplaintMode || isDeviceComplaintHistoryMode) return true;
    return !complaintScopedDocumentTypes.has(opt.value);
  });
  const visibleDocumentOptions = resolvedDocumentOptions.filter((opt) =>
    allowedDocumentTypes ? allowedDocumentTypes.includes(opt.value) : true
  );
  const uploadSelectOptions =
    uploadDocumentOptions ?? visibleDocumentOptions;
  const defaultDocumentType = uploadSelectOptions?.[0]?.value ?? 'manual';

  const effectivePermissions = {
    // Always keep approval-review restrictions enforced (matches existing behavior)
    // Device complaint history is always read-only.
    canUpload:
      !isDeviceComplaintHistoryMode
      && (permissions?.canUpload ?? canUploadDocuments)
      && !isAwaitingApprovalReview,
    canDelete:
      !isDeviceComplaintHistoryMode
      && (permissions?.canDelete ?? canDeleteDocuments)
      && !isAwaitingApprovalReview,
    canPreview: permissions?.canPreview ?? canPreviewDocuments,
  };

  const isTypeAllowed = (docType) => {
    if (!allowedDocumentTypes) return true;
    return allowedDocumentTypes.includes(docType);
  };

  const isSessionDocument = (doc) => {
    if (sessionBaselineDocumentIdsRef.current === null) return false;
    return !sessionBaselineDocumentIdsRef.current.has(doc.id);
  };

  const isBaselineDocument = (doc) => {
    if (sessionBaselineDocumentIdsRef.current === null) return false;
    return sessionBaselineDocumentIdsRef.current.has(doc.id);
  };

  const visibleDocuments = documents.filter((d) => {
    if (!isTypeAllowed(d.document_type)) return false;
    if (showOnlySessionDocuments) return isSessionDocument(d);
    if (showOnlyBaselineDocuments) return isBaselineDocument(d);
    return true;
  });

  const canDeleteThisDocument = (doc) => {
    if (!effectivePermissions.canDelete) return false;
    if (restrictDeleteToNewSinceInitialLoad) {
      if (!isSessionDocument(doc)) return false;
    }
    if (!deleteOnlyOwn) return true;
    return doc?.uploaded_by_employee_id && user?.employee_id
      ? doc.uploaded_by_employee_id === user.employee_id
      : false;
  };

  /** Match complaint reporter badges: Registered (system user) vs Manual (guest entry). */
  const renderUploaderLabel = (doc) => {
    if (doc.uploaded_by_employee_id && doc.uploaded_by_employee_id === user?.employee_id) {
      return (
        <span className="doc-uploader-you" title="Uploaded by you">
          You
        </span>
      );
    }
    const name = doc.full_name || doc.uploaded_by_employee_id || 'Unknown';
    const isManual = Boolean(doc.uploaded_by_is_guest);
    return (
      <>
        <span className="doc-uploader-name">{name}</span>
        <span
          className={`doc-uploader-badge ${isManual ? 'manual' : 'registered'}`}
          title={
            isManual
              ? 'Manual: Uploaded with guest/manual reporter details (not a registered system user)'
              : 'Registered: Uploaded by or attributed to a system user account'
          }
        >
          {isManual ? 'Manual' : 'Registered'}
        </span>
      </>
    );
  };

  useEffect(() => {
    if (complaintId || deviceId) {
      sessionBaselineDocumentIdsRef.current = null;
      fetchDocuments();
    }
  }, [deviceId, complaintId, excludeComplaintId, listDocumentType, showOnlySessionDocuments, showOnlyBaselineDocuments, instanceId]);

  useEffect(() => {
    const allowedValues = uploadSelectOptions.map((o) => o.value);
    if (!allowedValues.length) return;
    if (!allowedValues.includes(documentType)) {
      setDocumentType(allowedValues[0]);
    }
  }, [allowedDocumentTypes, documentOptions, documentType, uploadDocumentOptions]);

  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && showPreviewModal) {
        closePreviewModal();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showPreviewModal]);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(listUrl(), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setDocuments(response.data);
      if (tracksSessionBaseline && sessionBaselineDocumentIdsRef.current === null) {
        sessionBaselineDocumentIdsRef.current = new Set(
          response.data.map((d) => d.id)
        );
      }
      if (
        tracksSessionBaseline &&
        onPastBaselineDocumentCountChange &&
        sessionBaselineDocumentIdsRef.current !== null
      ) {
        const baseline = sessionBaselineDocumentIdsRef.current;
        const pastBaselineCount = response.data.filter((d) => !baseline.has(d.id))
          .length;
        onPastBaselineDocumentCountChange(pastBaselineCount);
      }
      if (onDocumentCountChange) {
        const baseline = sessionBaselineDocumentIdsRef.current;
        const visibleCount = response.data.filter((d) => {
          if (!isTypeAllowed(d.document_type)) return false;
          if (showOnlySessionDocuments) {
            return baseline ? !baseline.has(d.id) : false;
          }
          if (showOnlyBaselineDocuments) {
            return baseline ? baseline.has(d.id) : false;
          }
          return true;
        }).length;
        onDocumentCountChange(visibleCount);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const updateDocTypeMenuPosition = () => {
    const trigger = docTypeRef.current?.querySelector('.doc-upload-trigger');
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const gap = 4;
    const maxMenuHeight = 280;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openUpward = spaceBelow < 160 && spaceAbove > spaceBelow;
    const available = openUpward ? spaceAbove : spaceBelow;

    setDocTypeMenuStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      top: openUpward ? undefined : rect.bottom + gap,
      bottom: openUpward ? window.innerHeight - rect.top + gap : undefined,
      maxHeight: Math.max(120, Math.min(maxMenuHeight, available)),
      zIndex: 10050,
    });
  };

  const toggleDocTypeMenu = () => {
    if (docTypeOpen) {
      setDocTypeOpen(false);
      setDocTypeMenuStyle(null);
      return;
    }
    updateDocTypeMenuPosition();
    setDocTypeOpen(true);
  };

  useEffect(() => {
    if (!docTypeOpen) return undefined;

    updateDocTypeMenuPosition();

    const handleClickOutside = (event) => {
      const inTrigger = docTypeRef.current?.contains(event.target);
      const inMenu = docTypeMenuRef.current?.contains(event.target);
      if (!inTrigger && !inMenu) {
        setDocTypeOpen(false);
        setDocTypeMenuStyle(null);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setDocTypeOpen(false);
        setDocTypeMenuStyle(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', updateDocTypeMenuPosition);
    window.addEventListener('scroll', updateDocTypeMenuPosition, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', updateDocTypeMenuPosition);
      window.removeEventListener('scroll', updateDocTypeMenuPosition, true);
    };
  }, [docTypeOpen]);

  const revokePendingPreviewUrl = (url) => {
    if (url) {
      window.URL.revokeObjectURL(url);
    }
  };

  const setPendingFilePreview = (file) => {
    revokePendingPreviewUrl(pendingPreviewUrlRef.current);
    pendingPreviewUrlRef.current = null;
    if (!file) {
      setPendingPreviewUrl(null);
      setShowPendingPreview(false);
      return;
    }
    const url = window.URL.createObjectURL(file);
    pendingPreviewUrlRef.current = url;
    setPendingPreviewUrl(url);
    const kind = (() => {
      const type = file.type || '';
      const name = (file.name || '').toLowerCase();
      if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp)$/i.test(name)) return 'image';
      if (type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
      return 'unsupported';
    })();
    // Auto-open preview for images/PDFs so the value is obvious before upload.
    setShowPendingPreview(kind !== 'unsupported');
  };

  useEffect(() => {
    return () => {
      revokePendingPreviewUrl(pendingPreviewUrlRef.current);
      pendingPreviewUrlRef.current = null;
    };
  }, []);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0] || null;
    setPendingFilePreview(file);
    setSelectedFile(file);
    setError('');
    setSuccess('');
  };

  const handleClearFile = () => {
    setPendingFilePreview(null);
    setSelectedFile(null);
    const input = document.getElementById(fileInputId);
    if (input) input.value = '';
    const cameraInput = document.getElementById(cameraInputId);
    if (cameraInput) cameraInput.value = '';
  };

  const getPendingPreviewKind = (file) => {
    if (!file) return 'none';
    const type = file.type || '';
    const name = (file.name || '').toLowerCase();
    if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp)$/i.test(name)) return 'image';
    if (type === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
    return 'unsupported';
  };

  const selectedTypeLabel =
    uploadSelectOptions.find((option) => option.value === documentType)?.label || documentType;

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('document_type', documentType);
    if (!isComplaintMode) {
      if (workType) {
        formData.append('work_type', workType);
      } else if (user?.role === 'technician') {
        formData.append('work_type', TECHNICIAN_WORK_TYPE_GENERAL);
      }
    }

    try {
      await axios.post(uploadUrl(), formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setSuccess('Document uploaded successfully!');
      setSelectedFile(null);
      setDocumentType(defaultDocumentType);
      handleClearFile();
      await fetchDocuments();

      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error uploading document:', error);
      setError(error.response?.data?.error || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handlePreview = async (doc) => {
    if (!isTypeAllowed(doc?.document_type)) {
      setError('You do not have access to preview this document type.');
      setTimeout(() => setError(''), 4000);
      return;
    }
    setSelectedDocumentForPreview(doc);
    setShowPreviewModal(true);
    setPreviewLoading(true);

    try {
      const response = await axios.get(resolveDocumentUrl(doc), {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: doc.mime_type.includes('pdf') ? 'blob' : 'arraybuffer'
      });

      const blob = new Blob([response.data], { type: doc.mime_type });
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (error) {
      console.error('Error previewing document:', error);
      setError('Failed to load document preview');
      setSelectedDocumentForPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreviewModal = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setShowPreviewModal(false);
    setSelectedDocumentForPreview(null);
    setPreviewUrl(null);
  };

  const handleDownload = async (doc, filename) => {
    try {
      const response = await fetch(resolveDocumentUrl(doc), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  const handleDelete = (doc, filename) => {
    // CRITICAL: Prevent deletion when device is awaiting approval
    if (isAwaitingApprovalReview) {
      setError('❌ Document deletions are disabled while this device is awaiting admin approval. Once the admin reviews and approves/rejects the device, you can modify documents.');
      setTimeout(() => setError(''), 5000);
      return;
    }

    // Show confirmation modal instead of window.confirm
    setDocumentToDelete({ doc, name: filename });
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (!documentToDelete?.doc) return;

    setIsDeletingDocument(true);

    try {
      await axios.delete(resolveDocumentUrl(documentToDelete.doc), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setSuccess('Document deleted successfully!');
      await fetchDocuments();
      setTimeout(() => setSuccess(''), 3000);
      setShowDeleteConfirmation(false);
      setDocumentToDelete(null);
    } catch (error) {
      console.error('Error deleting document:', error);
      setError(error.response?.data?.error || 'Failed to delete document');
    } finally {
      setIsDeletingDocument(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
    setDocumentToDelete(null);
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '—';
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const getDocIcon = (mimeType) => {
    if (mimeType.includes('pdf')) return '📄';
    if (mimeType.includes('image')) return '🖼️';
    if (mimeType.includes('word')) return '📝';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return '📊';
    return '📎';
  };

  return (
    <div
      className={`document-manager${isDeviceComplaintHistoryMode ? ' document-manager--device-complaint-history' : ''}`}
      ref={managerRef}
    >
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Full Restriction Banner - Show when device is submitted for approval */}
      {isAwaitingApprovalReview && (
        <div style={{
          padding: '12px 15px',
          backgroundColor: '#e3f2fd',
          borderRadius: '4px',
          marginBottom: '15px',
          border: '1px solid #2196F3',
          display: 'flex',
          gap: '10px',
          alignItems: 'flex-start'
        }}>
          <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>🔒</span>
          <div>
            <p style={{ margin: '0 0 5px 0', fontWeight: '500', color: '#1565c0' }}>Documents are read-only</p>
            <p style={{ margin: '0', color: '#1565c0', fontSize: '0.9rem' }}>This device is awaiting admin approval. Document uploads and deletions are disabled during the review process. You can preview and download existing documents.</p>
          </div>
        </div>
      )}

      {/* Upload Restriction Banner - Show when manager tries to upload for pending device */}
      {isDevicePendingApproval && user?.role === 'manager' && !isAwaitingApprovalReview && (
        <div style={{
          padding: '12px 15px',
          backgroundColor: '#fff3e0',
          borderRadius: '4px',
          marginBottom: '15px',
          border: '1px solid #ffe0b2',
          display: 'flex',
          gap: '10px',
          alignItems: 'flex-start'
        }}>
          <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>🔒</span>
          <div>
            <p style={{ margin: '0 0 5px 0', fontWeight: '500', color: '#e65100' }}>Document uploads are restricted</p>
            <p style={{ margin: '0', color: '#666', fontSize: '0.9rem' }}>Documents can be uploaded once this device is approved by an administrator.</p>
          </div>
        </div>
      )}

      {/* Upload Section - For admin, superadmin, coordinator, and manager users (if device is approved) */}
      {effectivePermissions.canUpload && (
        <div className="upload-form">
          <div className="upload-toolbar">
            <div className="upload-toolbar-field upload-toolbar-field--type">
              <label id="document-type-label" className="upload-toolbar-label">
                Document Type
              </label>
              <div className="doc-type-dropdown" ref={docTypeRef}>
                <button
                  type="button"
                  id="document-type"
                  className={`doc-upload-trigger ${docTypeOpen ? 'open' : ''}`}
                  onClick={toggleDocTypeMenu}
                  aria-haspopup="listbox"
                  aria-expanded={docTypeOpen}
                  aria-labelledby="document-type-label"
                >
                  <span className="doc-upload-trigger-label">{selectedTypeLabel}</span>
                  <span className="doc-upload-trigger-chevron" aria-hidden="true">
                    {docTypeOpen ? '▲' : '▼'}
                  </span>
                </button>
                {docTypeOpen &&
                  docTypeMenuStyle &&
                  ReactDOM.createPortal(
                    <div
                      ref={docTypeMenuRef}
                      className="doc-type-dropdown-menu doc-type-dropdown-menu--portal"
                      role="listbox"
                      style={docTypeMenuStyle}
                    >
                      {uploadSelectOptions.map((option) => {
                        const isSelected = option.value === documentType;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            role="option"
                            aria-selected={isSelected}
                            className={`doc-type-dropdown-option ${isSelected ? 'selected' : ''}`}
                            onClick={() => {
                              setDocumentType(option.value);
                              setDocTypeOpen(false);
                              setDocTypeMenuStyle(null);
                            }}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>,
                    document.body
                  )}
              </div>
            </div>

            <div className="upload-toolbar-field upload-toolbar-field--file">
              {enableCameraCapture ? (
                <span className="upload-toolbar-label">Add report</span>
              ) : (
                <span className="upload-toolbar-label" id="select-file-label">
                  File
                </span>
              )}
              {enableCameraCapture ? (
                <div className="file-source-actions">
                  {takePhotoEnabled && !uploading ? (
                    <label
                      htmlFor={cameraInputId}
                      className="file-source-btn file-source-btn--primary"
                    >
                      <input
                        id={cameraInputId}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                        capture="environment"
                        onChange={handleFileSelect}
                      />
                      <span aria-hidden="true">📷</span>
                      Take photo
                    </label>
                  ) : (
                    <button
                      type="button"
                      className="file-source-btn file-source-btn--primary disabled"
                      disabled
                      title={uploading ? 'Upload in progress' : takePhotoTitle}
                      aria-label={uploading ? 'Upload in progress' : takePhotoTitle}
                    >
                      <span aria-hidden="true">📷</span>
                      Take photo
                    </button>
                  )}
                  <label
                    htmlFor={fileInputId}
                    className={`file-source-btn file-source-btn--secondary ${uploading ? 'disabled' : ''}`}
                  >
                    <input
                      id={fileInputId}
                      type="file"
                      onChange={handleFileSelect}
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt,.csv"
                      disabled={uploading}
                    />
                    <span aria-hidden="true">🖼️</span>
                    Choose file
                  </label>
                </div>
              ) : (
                <div className={`doc-file-picker ${uploading ? 'disabled' : ''}`}>
                  <input
                    id={fileInputId}
                    type="file"
                    onChange={handleFileSelect}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt,.csv"
                    className="doc-file-input-hidden"
                    disabled={uploading}
                    aria-labelledby="select-file-label"
                  />
                  <label htmlFor={fileInputId} className="doc-file-btn">
                    {selectedFile ? 'Change file' : 'Choose file'}
                  </label>
                </div>
              )}
            </div>

            <div className="upload-toolbar-field upload-toolbar-field--action">
              <span className="upload-toolbar-label upload-toolbar-label--spacer" aria-hidden="true">
                Upload
              </span>
              <button
                type="button"
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                className="btn-upload"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>

          {!enableCameraCapture && (
            <div className="doc-file-help">
              {selectedFile ? (
                <>
                  <span className="doc-file-help-name" title={selectedFile.name}>
                    {selectedFile.name}
                  </span>
                  <span className="doc-file-help-actions">
                    <button
                      type="button"
                      className="doc-file-help-link"
                      onClick={() => setShowPendingPreview((open) => !open)}
                      aria-pressed={showPendingPreview}
                    >
                      {showPendingPreview ? 'Hide preview' : 'Preview'}
                    </button>
                    <button
                      type="button"
                      className="doc-file-help-link doc-file-help-link--danger"
                      onClick={handleClearFile}
                    >
                      Remove
                    </button>
                  </span>
                </>
              ) : (
                <span>PDF, Office, or images. You can preview before uploading.</span>
              )}
            </div>
          )}

          {enableCameraCapture && selectedFile && (
            <div className="selected-file-container">
              <span className="selected-file">{selectedFile.name}</span>
              <button
                type="button"
                className="doc-file-help-link"
                onClick={() => setShowPendingPreview((open) => !open)}
                aria-pressed={showPendingPreview}
              >
                {showPendingPreview ? 'Hide preview' : 'Preview'}
              </button>
              <button
                type="button"
                onClick={handleClearFile}
                className="btn-clear-file"
                title="Clear selection"
              >
                ✕
              </button>
            </div>
          )}

          {selectedFile && showPendingPreview && (
            <div className="doc-pending-preview" aria-live="polite">
              <div className="doc-pending-preview-header">
                <div className="doc-pending-preview-meta">
                  <strong>{selectedFile.name}</strong>
                  <span>
                    {formatFileSize(selectedFile.size)}
                    {selectedFile.type ? ` · ${selectedFile.type}` : ''}
                  </span>
                </div>
                <button
                  type="button"
                  className="doc-pending-preview-close"
                  onClick={() => setShowPendingPreview(false)}
                  aria-label="Close preview"
                >
                  ✕
                </button>
              </div>
              <div className="doc-pending-preview-body">
                {getPendingPreviewKind(selectedFile) === 'image' && pendingPreviewUrl ? (
                  <img
                    src={pendingPreviewUrl}
                    alt={`Preview of ${selectedFile.name}`}
                    className="doc-pending-preview-image"
                  />
                ) : getPendingPreviewKind(selectedFile) === 'pdf' && pendingPreviewUrl ? (
                  <iframe
                    src={pendingPreviewUrl}
                    title={`Preview of ${selectedFile.name}`}
                    className="doc-pending-preview-pdf"
                  />
                ) : (
                  <div className="doc-pending-preview-unavailable">
                    <p>Preview not available for this file type</p>
                    <p className="doc-pending-preview-unavailable-detail">
                      {selectedFile.name}
                      {selectedFile.type ? ` (${selectedFile.type})` : ''}
                      {' · '}
                      {formatFileSize(selectedFile.size)}
                    </p>
                    {pendingPreviewUrl && (
                      <a
                        className="doc-pending-preview-open"
                        href={pendingPreviewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Open in new tab
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Documents List Section - Minimal Table Design */}
      <div className="documents-list">
        {visibleDocuments.length === 0 ? (
          <div className="empty-state-container">
            <p className="empty-state-text">
              {emptyStateText
                || (isComplaintMode
                  ? 'No documents for this complaint yet.'
                  : 'No documents uploaded yet. Start by uploading device documentation.')}
            </p>
          </div>
        ) : (
          <div className="documents-table-wrapper">
            <div className="documents-table-header">
              {isDeviceComplaintHistoryMode && (
                <div className="table-header-cell header-complaint">Complaint</div>
              )}
              <div className="table-header-cell header-name">File Name</div>
              <div className="table-header-cell header-type">Type</div>
              <div className="table-header-cell header-size">Size</div>
              <div className="table-header-cell header-date">Date</div>
              <div className="table-header-cell header-actions">Actions</div>
            </div>
            <div className="documents-table-body">
              {visibleDocuments.map((doc) => (
                <div key={doc.id} className="table-row">
                  {isDeviceComplaintHistoryMode && (
                    <div className="table-cell cell-complaint">
                      <div className="complaint-stack">
                        <ComplaintIDBadge
                          complaintId={doc.complaint_identifier || `Complaint #${doc.complaint_id}`}
                          size="small"
                        />
                        {doc.complaint_status && (
                          <StatusBadge status={doc.complaint_status} size="small" />
                        )}
                      </div>
                    </div>
                  )}
                  <div className="table-cell cell-name">
                    <span className="file-icon">{doc.mime_type.includes('pdf') ? '📄' :
                       doc.mime_type.includes('image') ? '🖼️' :
                       doc.mime_type.includes('word') ? '📝' :
                       doc.mime_type.includes('excel') || doc.mime_type.includes('spreadsheet') ? '📊' :
                       '📎'}</span>
                    <span className="file-name-text" title={doc.file_name}>{doc.file_name}</span>
                  </div>
                  <div className="table-cell cell-type">
                    <span className="type-badge">{formatDocumentType(doc.document_type)}</span>
                  </div>
                  <div className="table-cell cell-size">{formatFileSize(doc.file_size)}</div>
                  <div className="table-cell cell-date">
                    <div className="date-main">{new Date(doc.created_at).toLocaleDateString()}</div>
                    <div className="date-user">
                      by {renderUploaderLabel(doc)}
                    </div>
                  </div>
                  <div className="table-cell cell-actions">
                    {effectivePermissions.canPreview ? (
                      <>
                        <button
                          type="button"
                          onClick={() => handlePreview(doc)}
                          className="doc-action-btn preview-btn"
                          title="Preview"
                        >
                          👁️
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDownload(doc, doc.file_name)}
                          className="doc-action-btn download-btn"
                          title="Download"
                        >
                          ⬇️
                        </button>
                      </>
                    ) : (
                      <span style={{ fontSize: '0.85rem', color: '#999' }}>Restricted</span>
                    )}
                    {canDeleteThisDocument(doc) && (
                      <button
                        type="button"
                        onClick={() => handleDelete(doc, doc.file_name)}
                        className="doc-action-btn delete-btn"
                        title={isAwaitingApprovalReview ? 'Document deletion disabled while awaiting admin approval' : 'Delete'}
                        disabled={isAwaitingApprovalReview}
                        style={{
                          opacity: isAwaitingApprovalReview ? 0.5 : 1,
                          pointerEvents: isAwaitingApprovalReview ? 'none' : 'auto',
                          cursor: isAwaitingApprovalReview ? 'not-allowed' : 'pointer'
                        }}
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        entityName={documentToDelete?.name || 'Document'}
        entityType="document"
        action="delete"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={isDeletingDocument}
        customMessage="This document will be removed from the device record. You can upload it again later if needed."
      />

      {/* Document Preview Modal - Two Panel Layout */}
      {showPreviewModal && ReactDOM.createPortal(
        <div
          className="modal-overlay"
          onClick={closePreviewModal}
        >
          <div
            className="documents-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Document Preview</h3>
              <button type="button" className="close-btn" onClick={closePreviewModal}>✕</button>
            </div>

            <div className="documents-modal-content">
              {/* Left Panel - Documents List */}
              <div className="documents-list-panel">
                <div className="documents-list-header">
                  <h4>{visibleDocuments.length} Document{visibleDocuments.length !== 1 ? 's' : ''}</h4>
                </div>
                {visibleDocuments.length > 0 ? (
                  <div className="documents-list-items">
                    {visibleDocuments.map((doc) => (
                      <div
                        key={doc.id}
                        className={`document-list-item ${selectedDocumentForPreview?.id === doc.id ? 'active' : ''}`}
                        onClick={() => handlePreview(doc)}
                      >
                        <div className="doc-item-icon">{getDocIcon(doc.mime_type)}</div>
                        <div className="doc-item-info">
                          <div className="doc-item-name">{doc.file_name}</div>
                          <div className="doc-item-meta">
                            {formatDocumentType(doc.document_type)} • {doc.file_size ? Math.round(doc.file_size / 1024) : '0'} KB
                          </div>
                        </div>
                        <div className="doc-item-preview-icon">👁️</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-documents">
                    <p>No documents found for this device.</p>
                  </div>
                )}
              </div>

              {/* Right Panel - Preview */}
              <div className="documents-preview-panel">
                {selectedDocumentForPreview ? (
                  <>
                    <div className="preview-header">
                      <h4>{selectedDocumentForPreview.file_name}</h4>
                      <button
                        type="button"
                        className="close-preview-btn"
                        onClick={() => {
                          setSelectedDocumentForPreview(null);
                          if (previewUrl) {
                            window.URL.revokeObjectURL(previewUrl);
                          }
                          setPreviewUrl(null);
                        }}
                        title="Close preview"
                      >
                        ✕
                      </button>
                    </div>
                    <div className="preview-content">
                      {previewLoading ? (
                        <div className="preview-loading">
                          <div className="spinner"></div>
                          <p>Loading document...</p>
                        </div>
                      ) : previewUrl ? (
                        <>
                          {selectedDocumentForPreview.mime_type.includes('pdf') ? (
                            <iframe
                              src={previewUrl}
                              className="pdf-viewer"
                              title="PDF Viewer"
                            ></iframe>
                          ) : selectedDocumentForPreview.mime_type.includes('image') ? (
                            <div className="image-viewer">
                              <img
                                src={previewUrl}
                                alt={selectedDocumentForPreview.file_name}
                                className="preview-image"
                              />
                            </div>
                          ) : (
                            <div className="document-unavailable">
                              <p>Preview not available for this file type</p>
                              <p className="file-info">{selectedDocumentForPreview.file_name}</p>
                              <button
                                type="button"
                                className="btn-download-preview"
                                onClick={() => {
                                  const link = document.createElement('a');
                                  link.href = previewUrl;
                                  link.download = selectedDocumentForPreview.file_name;
                                  link.click();
                                }}
                              >
                                ⬇️ Download File
                              </button>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="preview-error">
                          <p>Failed to load document</p>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="preview-empty">
                    <p>Select a document to preview</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default DocumentManager;
